import PropTypes from 'prop-types';

/**
 * Core
 */

export const translation = PropTypes.string;
export const translations = PropTypes.oneOfType([
    PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),
    PropTypes.objectOf(PropTypes.string),
]);

export const routes = PropTypes.objectOf(PropTypes.string);

export const intl = PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
});

export const message = PropTypes.shape({
    id: PropTypes.string,
    defaultMessage: PropTypes.string,
});

export const text = PropTypes.oneOfType([message, PropTypes.string]);

export const label = PropTypes.oneOfType([message, PropTypes.node]);

export const statusCode = PropTypes.oneOf([404, 401, 403, 500]);

export const ref = PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
        current: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    }),
]);

export const components = PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
);

/**
 * Forms
 */
export const errors = PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]);
export const formErrors = PropTypes.objectOf(errors);

export const selectOption = PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
});
export const selectOptions = PropTypes.arrayOf(selectOption);

export const formField = PropTypes.shape({
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    name: PropTypes.string,
});
export const formFields = PropTypes.objectOf(formField);

/**
 * Auth
 */
export const user = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
});

/**
 * Menu
 */
export const menuItem = PropTypes.shape({
    id: PropTypes.string,
    label,
    url: PropTypes.string,
    external: PropTypes.bool,
    active: PropTypes.bool,
});
export const menuItems = PropTypes.arrayOf(menuItem);
export const menus = PropTypes.oneOfType([PropTypes.arrayOf(menuItems), menuItems]);

/**
 * Resources
 */
export const date = PropTypes.string;

export const dates = PropTypes.arrayOf(date);

export const localized = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string),
]);

export const credit = PropTypes.shape({
    name: PropTypes.string,
    year: PropTypes.string,
    origin: PropTypes.string,
});

export const imageSize = PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    credit,
});
export const imageSizes = PropTypes.arrayOf(imageSize);

export const image = PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    sizes: imageSizes,
});
export const images = PropTypes.arrayOf(image);

export const video = PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    duration: PropTypes.number,
});
export const videos = PropTypes.arrayOf(video);

export const pageMetadata = PropTypes.shape({
    description: PropTypes.string,
    keywords: PropTypes.arrayOf(PropTypes.string),
    image: PropTypes.oneOfType([PropTypes.string, image]),
    favicon: PropTypes.string,
});

export const page = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    url: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    image: PropTypes.oneOfType([PropTypes.string, image]),
    metadata: pageMetadata,
});
export const pages = PropTypes.arrayOf(page);

export const block = PropTypes.shape({
    id: PropTypes.string.isRequired,
});

export const blocks = PropTypes.arrayOf(block);

export const embed = PropTypes.shape({
    url: PropTypes.string.isRequired,
});

export const notification = PropTypes.shape({
    text: PropTypes.string.isRequired,
    image,
});

export const arrowDirection = PropTypes.oneOf(['right', 'left', 'top', 'bottom']);

export const notifications = PropTypes.objectOf(notification);

export const author = PropTypes.shape({ name: PropTypes.string, image, url: PropTypes.string });

export const edition = PropTypes.shape({
    id: PropTypes.string,
});

export const venue = PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    zip: PropTypes.string,
    latitude: PropTypes.string,
    longitude: PropTypes.string,
    telephone: PropTypes.string,
    link: PropTypes.string,
});

export const occurrence = PropTypes.shape({
    venue,
    date, // obviously not..
});
export const occurrences = PropTypes.arrayOf(occurrence);

export const article = PropTypes.shape({
    author,
    description: PropTypes.string,
    handle: PropTypes.string,
    id: PropTypes.string,
    image,
    slug: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
});

export const event = PropTypes.shape({
    edition,
    id: PropTypes.string,
    type: PropTypes.string,
    image,
    slug: PropTypes.string,
    url: PropTypes.string,
    venue,
});

export const events = PropTypes.arrayOf(event);

export const film = PropTypes.shape({
    id: PropTypes.string,
    slug: PropTypes.string,
    url: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    description: PropTypes.string,
    image,
    type: PropTypes.string,
});

export const films = PropTypes.arrayOf(film);

export const listItem = PropTypes.oneOfType([article, event, film]);
export const listItems = PropTypes.arrayOf(listItem);

export const filterOption = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
});
export const filterOptions = PropTypes.arrayOf(filterOption);

export const filter = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    options: filterOptions,
});
export const filters = PropTypes.arrayOf(filter);

export const teamMember = PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    image: PropTypes.string,
});

export const teamMembers = PropTypes.arrayOf(teamMember);

export const link = PropTypes.shape({
    label: PropTypes.node,
    href: PropTypes.string,
    route: PropTypes.string,
    routeParams: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    external: PropTypes.bool,
    target: PropTypes.string,
});

export const favorite = PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
});
export const favorites = PropTypes.arrayOf(favorite);

export const slide = PropTypes.shape({
    type: PropTypes.oneOf(['home', 'article', 'event', 'film', 'slide']),
    article,
    event,
    film,
    title: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string,
    buttonLabel: PropTypes.string,
    image,
});
export const slides = PropTypes.arrayOf(slide);

export const product = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    amount: PropTypes.number,
    currency: PropTypes.string,
});
export const products = PropTypes.arrayOf(product);

export const partner = PropTypes.shape({
    name: PropTypes.string,
    image,
    url: PropTypes.string,
});
export const partners = PropTypes.arrayOf(partner);

export const purchase = PropTypes.shape({
    id: PropTypes.string,
});
export const purchases = PropTypes.arrayOf(purchase);

export const onlineFilm = PropTypes.shape({
    id: PropTypes.string,
    filmId: PropTypes.string,
    userId: PropTypes.string,
});
export const onlineFilms = PropTypes.arrayOf(onlineFilm);

export const pagination = PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
    pagination: PropTypes.shape({
        page: PropTypes.number,
        per_page: PropTypes.number,
        last_page: PropTypes.number,
        total: PropTypes.number,
    }),
});
